import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useMatch } from "@tanstack/react-router";
import { apiFetch } from "@/lib/api";
import BarcodeScanner from "@/components/BarcodeScanner";
import FieldClear from "@/components/FieldClear";
import ImageUploadZone from "@/components/admin/ImageUploadZone";
import PriceCalculator from "@/components/admin/PriceCalculator";
import ConditionSelector from "@/components/admin/ConditionSelector";
import QuantityInput from "@/components/admin/QuantityInput";
import StickerPrintControls from "@/components/admin/StickerPrintControls";
import { allSoslineFromLunatik, soslinePriceFromLunatik } from "@/lib/lunatikPricing";
import {
  ArrowLeft,
  Package,
  MapPin,
  Check,
  ScanBarcode,
  Wand2,
} from "lucide-react";

interface Category {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  sku: string | null;
  price: number;
  sale_price: number | null;
  condition: number;
  is_new: number;
  category_id: number | null;
  quantity: number;
  size: string | null;
  bin_location: string | null;
  image: string | null;
}

export default function ProductForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const editMatch = useMatch({ from: "/admin/products/$id/edit", shouldThrow: false });
  const editId = editMatch?.params?.id;
  const isEdit = !!editId;

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [condition, setCondition] = useState(3);
  const [isNew, setIsNew] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState("");
  const [binLocation, setBinLocation] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [lunatikPrice, setLunatikPrice] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [scannerTarget, setScannerTarget] = useState<"sku" | "bin" | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [skuGenerateError, setSkuGenerateError] = useState<string | null>(null);
  const [isGeneratingSku, setIsGeneratingSku] = useState(false);
  const [printMessage, setPrintMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printQuantity, setPrintQuantity] = useState(1);

  const { data: categories = [] } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => apiFetch<Category[]>("/api/admin/categories"),
  });

  const { data: product } = useQuery({
    queryKey: ["admin-product", editId],
    queryFn: () => apiFetch<Product>(`/api/admin/products/${editId}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!isEdit || !product) return;
    setName(product.name);
    setSku(product.sku ?? "");
    setPrice((product.price / 100).toFixed(2));
    setSalePrice(product.sale_price != null ? (product.sale_price / 100).toFixed(2) : "");
    setCondition(product.condition);
    setIsNew(product.is_new === 1);
    setCategoryId(product.category_id != null ? String(product.category_id) : "");
    setQuantity(product.quantity);
    setSize(product.size ?? "");
    setBinLocation(product.bin_location ?? "");
    if (product.image) {
      setImagePreview(`/uploads/${product.id}-thumb.webp`);
    }
  }, [isEdit, product]);

  useEffect(() => {
    if (!isEdit) nameInputRef.current?.focus();
  }, [isEdit]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setImageFile(file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  }

  function removeImage() {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }

  const lunatikParsed = useMemo(() => {
    const t = lunatikPrice.trim().replace(",", ".");
    if (t === "") return null;
    const n = parseFloat(t);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [lunatikPrice]);

  const suggestedSosline =
    lunatikParsed != null ? soslinePriceFromLunatik(lunatikParsed, condition) : null;
  const soslineTable = lunatikParsed != null ? allSoslineFromLunatik(lunatikParsed) : null;

  const submitMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("sku", sku);
      formData.append("price", String(Math.round(parseFloat(price) * 100)));
      if (salePrice) formData.append("salePrice", String(Math.round(parseFloat(salePrice) * 100)));
      formData.append("condition", String(condition));
      formData.append("isNew", isNew ? "1" : "0");
      if (categoryId) formData.append("categoryId", categoryId);
      formData.append("quantity", String(quantity));
      formData.append("size", size);
      if (binLocation) formData.append("binLocation", binLocation);
      if (imageFile) formData.append("image", imageFile);

      const url = isEdit ? `/api/admin/products/${editId}` : "/api/admin/products";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, { method, body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((err as any).error || res.statusText);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      if (isEdit && editId) {
        queryClient.invalidateQueries({ queryKey: ["admin-product", editId] });
      }
      navigate({ to: "/admin/products" });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitMutation.mutate();
  }

  async function handleGenerateSku() {
    setSkuGenerateError(null);
    setIsGeneratingSku(true);
    try {
      const { sku: nextSku } = await apiFetch<{ sku: string }>("/api/admin/products/next-sku");
      setSku(nextSku);
    } catch (error) {
      setSkuGenerateError(error instanceof Error ? error.message : "Generisanje SKU-a nije uspjelo.");
    } finally {
      setIsGeneratingSku(false);
    }
  }

  async function handlePrintSku() {
    const trimmedSku = sku.trim();
    if (!trimmedSku) return;

    setPrintMessage(null);
    setIsPrinting(true);
    try {
      await apiFetch("/api/admin/print-sku", {
        method: "POST",
        body: JSON.stringify({ text: trimmedSku, quantity: printQuantity }),
      });
      setPrintMessage({
        type: "success",
        text: `Štampano ${printQuantity}× ${trimmedSku}`,
      });
      setTimeout(() => setPrintMessage(null), 3000);
    } catch (error) {
      setPrintMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Štampanje nije uspjelo.",
      });
      setTimeout(() => setPrintMessage(null), 5000);
    } finally {
      setIsPrinting(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto pb-28">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate({ to: "/admin/products" })}
          className="size-10 rounded-xl bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors active:scale-95"
        >
          <ArrowLeft className="size-5 text-neutral-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold tracking-tight truncate">
            {isEdit ? "Uredi proizvod" : "Novi proizvod"}
          </h1>
          {isEdit && (
            <p className="text-xs text-muted-foreground">ID: {editId}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <ImageUploadZone
          imagePreview={imagePreview}
          fileInputRef={fileInputRef}
          cameraInputRef={cameraInputRef}
          onFileChange={handleFileChange}
          onRemove={removeImage}
        />

        <div>
          <label htmlFor="name" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            Naziv proizvoda
          </label>
          <div className="relative">
            <input
              ref={nameInputRef}
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="npr. Nike Air Max 90"
              className={`w-full h-12 rounded-xl border border-border bg-white text-base font-medium placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition ${
                name.trim() ? "pl-4 pr-9" : "px-4"
              }`}
            />
            <FieldClear show={name.trim() !== ""} onClear={() => setName("")} />
          </div>
        </div>

        <div>
          <label htmlFor="sku" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            SKU
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                id="sku"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                required
                placeholder="SL00001"
                className={`w-full h-12 rounded-xl border border-border bg-white text-sm font-mono placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition ${
                  sku.trim() ? "pl-4 pr-9" : "px-4"
                }`}
              />
              <FieldClear show={sku.trim() !== ""} onClear={() => setSku("")} />
            </div>
            <button
              type="button"
              onClick={handleGenerateSku}
              disabled={isGeneratingSku}
              className="size-12 shrink-0 rounded-xl border-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center transition active:scale-95 disabled:opacity-40"
              title="Generiši SKU"
            >
              <Wand2 className={`size-5 text-neutral-600 ${isGeneratingSku ? "animate-pulse" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setScannerTarget("sku")}
              className="size-12 shrink-0 rounded-xl border-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center transition active:scale-95"
              title="Skeniraj barkod"
            >
              <ScanBarcode className="size-5 text-neutral-600" />
            </button>
          </div>
          {skuGenerateError && (
            <p className="text-destructive text-xs mt-1.5">{skuGenerateError}</p>
          )}
          {sku.trim() && (
            <StickerPrintControls
              quantity={printQuantity}
              onQuantityChange={setPrintQuantity}
              onPrint={handlePrintSku}
              canPrint
              isPrinting={isPrinting}
            />
          )}
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            Kategorija
          </label>
          <div className="relative">
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={`w-full h-12 rounded-xl border border-border bg-white text-sm focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition appearance-none cursor-pointer ${
                categoryId ? "pl-4 pr-9" : "px-4"
              }`}
            >
              <option value="">Odaberi...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
              ))}
            </select>
            <FieldClear
              show={categoryId !== ""}
              onClear={() => setCategoryId("")}
            />
          </div>
        </div>

        <div>
          <label htmlFor="size" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            Veličina
          </label>
          <div className="relative">
            <input
              id="size"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="npr. M, L, 42, 38-40"
              className={`w-full h-12 rounded-xl border border-border bg-white text-sm placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition ${
                size.trim() ? "pl-4 pr-9" : "px-4"
              }`}
            />
            <FieldClear show={size.trim() !== ""} onClear={() => setSize("")} />
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isNew}
            onChange={(e) => setIsNew(e.target.checked)}
            className="size-4 accent-foreground"
          />
          <span className="text-sm font-medium">Novi artikl (bez stanja oštećenja)</span>
        </label>

        {!isNew && <ConditionSelector value={condition} onChange={setCondition} />}

        <div className="rounded-2xl border border-border bg-white p-4 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="size-6 rounded-lg bg-emerald-50 flex items-center justify-center">
              <span className="text-xs font-bold text-emerald-600">KM</span>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cijene
            </span>
          </div>

          <PriceCalculator
            lunatikPrice={lunatikPrice}
            onLunatikPriceChange={setLunatikPrice}
            suggestedSosline={suggestedSosline}
            soslineTable={soslineTable}
            condition={condition}
            onApplySosline={() => setPrice(suggestedSosline!.toFixed(2))}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="price" className="text-xs text-muted-foreground mb-1 block">
                Cijena
              </label>
              <div className="relative">
                <input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                  placeholder="0.00"
                  className={`w-full h-12 rounded-xl border border-border bg-neutral-50 pl-4 text-lg font-semibold tabular-nums placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground focus:bg-white outline-none transition ${
                    price.trim() ? "pr-16" : "pr-9"
                  }`}
                />
                <FieldClear
                  show={price.trim() !== ""}
                  onClear={() => setPrice("")}
                  className="right-7"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-neutral-400 pointer-events-none">KM</span>
              </div>
            </div>
            <div>
              <label htmlFor="salePrice" className="text-xs text-muted-foreground mb-1 block">
                Akcijska
              </label>
              <div className="relative">
                <input
                  id="salePrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  placeholder="—"
                  className={`w-full h-12 rounded-xl border border-border bg-neutral-50 pl-4 text-lg font-semibold tabular-nums placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground focus:bg-white outline-none transition ${
                    salePrice.trim() ? "pr-16" : "pr-9"
                  }`}
                />
                <FieldClear
                  show={salePrice.trim() !== ""}
                  onClear={() => setSalePrice("")}
                  className="right-7"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-neutral-400 pointer-events-none">KM</span>
              </div>
            </div>
          </div>

          {price && salePrice && parseFloat(salePrice) < parseFloat(price) && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium">
              <Check className="size-3.5" />
              Ušteda {(parseFloat(price) - parseFloat(salePrice)).toFixed(2)} KM
              ({Math.round(((parseFloat(price) - parseFloat(salePrice)) / parseFloat(price)) * 100)}%)
            </div>
          )}
        </div>

        <div className="space-y-5">
          <QuantityInput value={quantity} onChange={setQuantity} />

          <div>
            <label htmlFor="binLocation" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
              Lokacija
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1 min-w-0">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-neutral-400 pointer-events-none z-0" />
                <input
                  id="binLocation"
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value)}
                  placeholder="A-12"
                  className={`w-full h-12 rounded-xl border border-border bg-white pl-10 text-sm font-mono placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition ${
                    binLocation.trim() ? "pr-9" : "pr-4"
                  }`}
                />
                <FieldClear show={binLocation.trim() !== ""} onClear={() => setBinLocation("")} />
              </div>
              <button
                type="button"
                onClick={() => setScannerTarget("bin")}
                className="size-12 shrink-0 rounded-xl border-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center transition active:scale-95"
                title="Skeniraj lokaciju"
              >
                <ScanBarcode className="size-5 text-neutral-600" />
              </button>
            </div>
          </div>
        </div>

        {submitMutation.isError && (
          <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600 font-medium">
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : "Došlo je do greške."}
          </div>
        )}

        {printMessage && (
          <div
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${
              printMessage.type === "error"
                ? "bg-red-50 border-red-100 text-red-600"
                : "bg-green-50 border-green-100 text-green-700"
            }`}
          >
            {printMessage.text}
          </div>
        )}
      </form>

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-t border-border/50 px-4 py-3 sm:pl-60">
        <div className="max-w-lg mx-auto flex gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: "/admin/products" })}
            className="h-12 px-5 rounded-xl border border-border bg-white text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition active:scale-[0.98]"
          >
            Otkaži
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            className="flex-1 h-12 rounded-xl bg-foreground text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition active:scale-[0.98] shadow-lg shadow-foreground/20"
          >
            {submitMutation.isPending ? (
              <>
                <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {isEdit ? "Spremam..." : "Kreiram..."}
              </>
            ) : (
              <>
                <Package className="size-4" />
                {isEdit ? "Spremi izmjene" : "Kreiraj proizvod"}
              </>
            )}
          </button>
        </div>
      </div>

      <BarcodeScanner
        open={scannerTarget !== null}
        onScan={(text) => {
          if (scannerTarget === "sku") setSku(text);
          else setBinLocation(text);
          setScannerTarget(null);
        }}
        onClose={() => setScannerTarget(null)}
        title={scannerTarget === "sku" ? "Skeniraj SKU" : "Skeniraj lokaciju"}
        hint="Usmjerite kameru prema barkodu na etiketi"
        error={scannerError}
      />
    </div>
  );
}
